import { Component, NO_ERRORS_SCHEMA, OnDestroy, OnInit, ViewContainerRef, ViewChild, TemplateRef } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule, ReactiveFormsModule, FormGroup } from '@angular/forms';
import { FaIconLibrary, FontAwesomeModule } from '@fortawesome/angular-fontawesome';
import { ButtonsModule } from '@progress/kendo-angular-buttons';
import { WindowCloseResult, DialogService, DialogsModule, WindowModule, WindowRef, WindowService } from '@progress/kendo-angular-dialog';
import { GridModule, PagerPosition, PagerType } from '@progress/kendo-angular-grid';
import { InputsModule } from '@progress/kendo-angular-inputs';
import { PagerModule } from '@progress/kendo-angular-pager';
import { MulticheckFilterComponent } from '../filters/multicheck-filter/multicheck-filter.component';
import { ErrorDialogModalComponent } from '../common-components/error-dialog-modal/error-dialog-modal.component';
import { DeleteConfirmComponent } from '../common-components/delete-confirm/delete-confirm.component';
import { BehaviorSubject, Observable, Subscription } from 'rxjs';
import { Release, Project, User, ProjectSummary } from '../models/models.lib';
import { CompositeFilterDescriptor, distinct, filterBy, orderBy, SortDescriptor } from '@progress/kendo-data-query';
import { CasiCloseOptions, FormModes, AppEnities, blastKeys, defaultGridPageSize } from '../common/constants.lib';
import { ToastrService } from 'ngx-toastr';
import { ProjectSummaryService } from './project-summary.service';
import {
  faPlusCircle, faAddressCard,
  faEdit, faTrash,
  faEye,
  faChartArea,
  faChartColumn,
  faCodePullRequest,
  faCodeMerge
} from '@fortawesome/free-solid-svg-icons';
import { setTitle } from '../common/utils.lib';
//import { EditRoleModalComponent } from './edit-role-modal/edit-role-modal.component';
import { all } from '@awesome.me/kit-787edf9aa0/icons';
import { LoggerService } from '../shared/logger.service';
import { MeService } from '../auth/me.service';
import { ActivatedRoute } from '@angular/router';
import { ReleaseListComponent } from '../release/release-list.component';
import { SocketService } from '../shared/socket.service';

@Component({
  selector: 'app-project-summary',
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    ReactiveFormsModule,
    FontAwesomeModule,
    InputsModule,
    ButtonsModule,
    DialogsModule,
    WindowModule,
    PagerModule,
    GridModule,
    ErrorDialogModalComponent,
    DeleteConfirmComponent,
    MulticheckFilterComponent,
    ReleaseListComponent
  ],
  schemas: [NO_ERRORS_SCHEMA],
  templateUrl: './project-summary.component.html',
  styleUrl: './project-summary.component.scss'
})
export class ProjectSummaryComponent implements OnInit, OnDestroy {

  // public view: Observable<Release[]> = new Observable<Release[]>();
  public rawData: ProjectSummary[] = [];
  public filter: CompositeFilterDescriptor = { logic: "and", filters: [] };
  public gridData: ProjectSummary[] = filterBy(this.rawData, this.filter);
  public currentProject: string | undefined;
  public currentProjectId: number | undefined;
  public editWinOpen: boolean = false;
  //  public projects: Project[] = [];
  // public users: User[] = [];
  public pagerTypes = ["numeric", "input"];
  public type: PagerType = "numeric";
  public info = true;
  public pageSizes = true;
  public previousNext = true;
  public position: PagerPosition = "bottom";

  public pageSize = defaultGridPageSize;

  private _subs: Subscription[] = [];

  // private _dialogRef: DialogRef | undefined;
  private _windowRef: WindowRef | undefined;
  @ViewChild("appendTo", { read: ViewContainerRef }) appendTo: ViewContainerRef | undefined;

  constructor(
    private logger: LoggerService,
    public dataService: ProjectSummaryService,
    public faIcons: FaIconLibrary,
    // private permissionService: PermissionService,
    private toastr: ToastrService,
    private windowService: WindowService,
    private me: MeService,
    private route: ActivatedRoute,
    private viewContainerRef: ViewContainerRef,
    private socketService: SocketService
  ) {
    faIcons.addIcons(
      faPlusCircle, faAddressCard, faEdit,
      faTrash, faChartColumn, faCodeMerge
    );
    faIcons.addIcons(...all);
  }

  public sort: SortDescriptor[] = [
    {
      field: "project_name",
      dir: "asc",
    },
  ];

  ngOnInit(): void {
    this.logger.debug('ngOnInit...');

    //   const { can_Create, can_Update, can_Destroy } = this.me.allICanDo(ROLE)
    //  this.canUpdate = can_Update;
    //  this.canCreate = can_Create;
    //  this.canDestroy = can_Destroy;

    this._subs.push(this.dataService.subscribe(v => {
      this.logger.debug('ngOnInit:v: ', v);
      this.rawData = v;
      this.loadData();
    }));
    this._subs.push(this.socketService.onRefresh()
      .subscribe((args: any | unknown) => {
        this.logger.debug(`onRefresh: `, args);
        if (args && (
          args.blastKey === blastKeys.USER_STORY_UPDATED ||
          args.blastKey === blastKeys.PROJECT_UPDATED
        )) {
          this.dataService.query();
        }
      }));

    this.dataService.query();
  }

  ngOnDestroy(): void {
    this.logger.debug('ngOnDestroy...');
    this._subs.forEach(s => s.unsubscribe());
  }

  //#region Grid function

  private compareStringValues(a: any, b: any, dir: "asc" | "desc" | undefined, fieldName: string) {
    const strA = String(a[fieldName]);
    const strB = String(b[fieldName]);
    const numA = parseInt(strA.replace(/\D/g, ''), 10);
    const numB = parseInt(strB.replace(/\D/g, ''), 10);
    // this.logger.debug(`compareStringValues: `, {
    //   a, b, dir, fieldName, strA, strB, numA, numB
    // });
    if (dir === 'desc') {
      return numB - numA;
    }
    return numA - numB;
  }

  public sortChange(sort: SortDescriptor[]): void {
    this.logger.debug("sortChange: ", sort);
    this.sort = sort;
    if (sort.length > 0 && sort[0].field !== 'project_name') {
      sort[0].compare = (a: any, b: any) => this.compareStringValues(a, b, sort[0].dir, sort[0].field);
    }
    this.loadData();
  }

  public filterChange(filter: CompositeFilterDescriptor): void {
    // this.logger.debug('filterChange: filter: ', filter);
    this.filter = filter;
    this.loadData();
  }

  public distinctPrimitive(fieldName: string): any[] {
    // this.logger.debug('distinctPrimitive: fieldName: ', fieldName);
    return distinct(this.rawData, fieldName).map((item: any) => item[fieldName]);
    // return distinct(this.gridData, fieldName).map((item: any) => item[fieldName]);
  }

  private loadData(): void {
    this.logger.debug('loadData...');
    this.gridData = orderBy(filterBy(this.rawData, this.filter), this.sort);
    this.pageSize = this.gridData.length;
  }

  public showSummary(dataItem: any) {

  }

  public showReleases(dataItem: any, titleBar: TemplateRef<unknown>) {
    this.currentProject = dataItem.project_name;
    this.closeWindowRef();
    this._windowRef = this.windowService.open({
      titleBarContent: titleBar,
      // title: `Manage Releases for ${dataItem.project_name}`,
      content: ReleaseListComponent,
      width: 1100,
      minWidth: 320
    });
    const contentRef = this._windowRef.content.instance as ReleaseListComponent;
    //  contentRef.title = setTitle(mode, 'RELEASE');
    contentRef.projectId = dataItem.project_id;
    contentRef.project = dataItem.project_name;

    this._windowRef.result.subscribe(results => {
      this.logger.debug('showAddWindow:results: ', results);
      if (results instanceof WindowCloseResult) {
        this.closeWindowRef();
        this.dataService.query();
      }
    });

    this._windowRef.content.instance.closeForm.subscribe((formData: FormGroup) => {
      this.logger.debug('showAddWindow:closeForm: formData: ', formData);
      this.closeWindowRef();
      this.dataService.query();
    });

    this.editWinOpen = true;
  }

  private closeWindowRef() {
    this.editWinOpen = false;
    this._windowRef?.close();
    this._windowRef = undefined;
  }

  // public showReleases(dataItem: any) {
  //   this.logger.debug("openAddEditDialog...");
  //   this._dialogRef = this.dialogService.open({
  //     content: ReleaseListComponent,
  //     minWidth: 320,
  //     width: 1000,
  //     maxHeight:'80%',
  //     appendTo: this.viewContainerRef
  //     // cssClass: "custom-css-class"
  //   });

  //   let contentRef = this._dialogRef.content.instance as ReleaseListComponent;
  //  // contentRef.title = `${dataItem.project_name} Releases`;
  //   contentRef.projectId = dataItem.project_id;
  //   contentRef.project = dataItem.project_name;

  //   this._dialogRef.result.subscribe((r: any) => {
  //     this.logger.debug('openAddEditDialog:result:', r);
  //     if (r instanceof DialogCloseResult) {
  //       this.closeDialogRef();
  //       this.dataService.query();
  //       return;
  //     }

  //   });
  // }

  // private closeDialogRef(): void {
  //   this.logger.info(`closeDialogRef...`)
  //   if (this._dialogRef) {
  //     this._dialogRef.close();
  //     this._dialogRef = undefined;
  //   }
  // }

  public close(): void {
    this.logger.debug('close...');
    this.editWinOpen = false;
    this.dataService.query();
  }

  //#endregion

}
